import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { getValidator } from 'src/Booking/utils/CreateAccountValidation';
import {
    elementNames,
    componentNames,
    elementMetricNames,
    EMAIL_VALIDATION_RESULT,
    errorNames,
} from './constants';
import { fetchEmailValidation } from './fetchEmailValidation';

/**
 *
 * @param {function} goBackToPatientInfo
 * @param {function} onSubmitSuccess
 * @param {function} sendMetric
 * @returns {{
 *  emailAlreadyInUseError: boolean,
 *  errors: {Object},
 *  goBackToPatientInfo,
 *  handleBlur: ((function(*=): Promise<void>)|*),
 *  handleChange: ((function(*=): Promise<void>)|*),
 *  handleClickSubmit: ((function(): Promise<void>)|*),
 *  handleFocus: ((function(*=): void)|*),
 *  highlightedFields: {Object},
 *  inlineEmailLoading: boolean,
 *  submitted: boolean,
 *  touched: {Object},
 *  values: {Object},
 * }}
 */
export const useCreateUsernameForm = ({
    goBackToPatientInfo,
    onSubmitSuccess,
    sendMetric,
}) => {
    const [focused, setFocused] = useState(null);

    const [touched, setTouched] = useState({
        [elementNames.email]: false,
    });
    const [values, setValues] = useState({
        [elementNames.email]: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [invalidFields, setInvalidFields] = useState({
        [elementNames.email]: true,
    });

    const emailError = useMemo(() => {
        return (
            touched[elementNames.email] &&
            (!values[elementNames.email] || invalidFields[elementNames.email])
        );
    }, [touched, values, invalidFields]);
    const [submitError, setSubmitError] = useState(false);

    const errors = {
        [errorNames.emailError]: emailError,
        [errorNames.submitError]: submitError,
    };

    const highlightedFields = useMemo(() => {
        return Object.entries(invalidFields).reduce(
            (highlightedFields, invalidFieldsEntry) => {
                const [name, isInvalid] = invalidFieldsEntry;
                const isHighlighted =
                    isInvalid && touched[name] && focused !== name;
                return { ...highlightedFields, [name]: isHighlighted };
            },
            {}
        );
    }, [focused, invalidFields, touched]);

    const [emailAlreadyInUseError, setEmailAlreadyInUseError] = useState(false);
    const [inlineEmailLoading, setInlineEmailLoading] = useState(false);

    const handleFocus = useCallback((e) => {
        if (e) {
            setFocused(e.target.name);
        }
    }, []);

    const handleChange = useCallback(async (e) => {
        if (e) {
            const { name, value } = e.target;
            /* values is an object React probably compares by reference,
             * so update it only if it really changed to avoid rerenders
             */

            setValues((values) =>
                values[name] === value ? values : { ...values, [name]: value }
            );
            const validate = getValidator(name);

            const isInvalid = !(await validate(value));

            /* invalidFields is an object React probably compares by reference,
             * so update it only if it really changed to avoid rerenders
             */
            setInvalidFields((invalidFields) =>
                invalidFields[name] === isInvalid
                    ? invalidFields
                    : {
                          ...invalidFields,
                          [name]: isInvalid,
                      }
            );

            // Each time the value of email field changes, revalidate confirm email field
            if (name === elementNames.email) {
                setEmailAlreadyInUseError(false); // clear email already in use error
            }
        }
    }, []);

    const handleBlur = useCallback(
        async (e) => {
            if (e) {
                /* touched is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */

                if (e.target.value) {
                    const element = elementMetricNames[e.target.name];
                    const component = componentNames[e.target.name];
                    sendMetric({
                        action: `Typed ${element.toLowerCase()}`,
                        component,
                        element,
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.TYPE,
                    });
                }

                const updatedTouches = (touched) =>
                    touched[e.target.name]
                        ? touched
                        : { ...touched, [e.target.name]: true };
                setTouched(updatedTouches);
            }

            setFocused(null);
        },
        [sendMetric]
    );

    const handleClickSubmit = useCallback(async () => {
        sendMetric({
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
            component: componentNames[elementNames.continue],
            element: elementMetricNames[elementNames.continue],
            action: 'Clicked continue button',
        });

        setSubmitted(true);
        setTouched({
            [elementNames.email]: true,
        });

        setInlineEmailLoading(true);

        const validationResult = await fetchEmailValidation(
            values[elementNames.email]
        );

        setInlineEmailLoading(false);

        if (validationResult === EMAIL_VALIDATION_RESULT.USED) {
            setEmailAlreadyInUseError(true);
            return;
        } else if (validationResult === EMAIL_VALIDATION_RESULT.UNUSED) {
            setEmailAlreadyInUseError(false);
        }

        const formHasInvalidFields = Object.entries(invalidFields)
            .map((entry) => {
                // eslint-disable-next-line no-unused-vars
                const [_, isInvalid] = entry;
                return isInvalid;
            })
            .some(Boolean);

        if (formHasInvalidFields) {
            return;
        }

        /* These errors can be set by request handler if necessary
         * on the next account creation request, but the current
         * value if any becomes irrelevant once we leave this page
         * because it's from the context of the last failed request
         */
        setEmailAlreadyInUseError(false);
        setSubmitError(false);
        onSubmitSuccess();
    }, [invalidFields, onSubmitSuccess, sendMetric, values]);

    useEffect(() => {
        if (emailError) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: elementMetricNames[elementNames.email],
                element: 'Error',
                action: 'Saw invalid email address error',
            });
        }
    }, [emailError, sendMetric]);

    useEffect(() => {
        if (emailAlreadyInUseError && !emailError) {
            // only when there's no email invalid error,
            // we show email already in use error
            sendMetric({
                action: 'Saw email already in use error',
                component: componentNames.emailAlreadyInUseError,
                element: 'Error',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }, [emailAlreadyInUseError, emailError, sendMetric]);

    return {
        errors,
        handleBlur,
        handleChange,
        handleClickSubmit,
        handleFocus,
        highlightedFields,
        goBackToPatientInfo,
        touched,
        values,
        submitted,
        inlineEmailLoading,
        emailAlreadyInUseError,
        setEmailAlreadyInUseError,
    };
};

export default useCreateUsernameForm;
